import { UntilDestroy } from '@ngneat/until-destroy';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TokenRefreshDataProvider } from '@auth/token-refresh/token-refresh.data-provider';
import { AuthState } from '@auth/state/models/auth.models';
import { Observable, of, throwError } from 'rxjs';
import { UnityCookieService } from '@utils/unity-cookie.service';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '@auth/authentication.service';
import { ActiveSecurityContextStateService } from '@auth/active-security-context.state-service';
import { AbstractEntityStateService } from '@entity-framework/entity-record/state/abstract-entity.state-service';
import { ErrorHandlerService } from '@utils/error-handler.service';
import { OnScreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { LoggingService } from '@logging/logging.service';

/**
 * Provides token refresh functionality for the active user with request queuing.
 */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class TokenRefreshService extends AbstractEntityStateService<AuthState> {
  private refreshInProgress$: Observable<AuthState | undefined> | null = null;

  constructor(
    private tokenRefreshDataProvider: TokenRefreshDataProvider,
    protected override route: ActivatedRoute,
    errorHandler: ErrorHandlerService,
    protected override messaging: OnScreenMessagingService,
    protected override logger: LoggingService,
    private unityCookieService: UnityCookieService,
    private authenticationService: AuthenticationService,
    private activeSecurityContextStateService: ActiveSecurityContextStateService
  ) {
    super(
      'TokenRefreshStore',
      tokenRefreshDataProvider,
      route,
      errorHandler,
      messaging,
      logger,
      undefined,
      {
        autoFetch: false,
        childEntity: false
      }
    );
  }

  /**
   * Restores session from cookies when in-memory auth state is missing.
   * Returns true when the user already has or successfully regains access.
   */
  ensureAuthenticated$(): Observable<boolean> {
    if (this.activeSecurityContextStateService.ensureUnityAccess()) {
      return of(true);
    }

    return this.refreshActiveUser$().pipe(
      map(
        (state) => !!state?.success && this.activeSecurityContextStateService.ensureUnityAccess()
      ),
      catchError(() => of(false))
    );
  }

  /**
   * Refreshes the active user's token with request queuing.
   * If a refresh is already in progress, subsequent calls will be queued.
   */
  refreshActiveUser$(): Observable<AuthState | undefined> {
    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.executeRefresh$().pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => {
          this.refreshInProgress$ = null;
        })
      );
    }

    return this.refreshInProgress$;
  }

  private executeRefresh$(): Observable<AuthState | undefined> {
    const refreshToken = this.unityCookieService.getRefreshToken();
    if (!refreshToken) {
      this.logger.error('No refresh token available');
      return of(undefined);
    }

    this.logger.debug('Attempting to refresh auth state');

    return this.tokenRefreshDataProvider.refreshActiveAuthState$(refreshToken).pipe(
      switchMap((newAuthState) => {
        if (!newAuthState?.success) {
          this.clearFailedRefreshState();
          return of(newAuthState);
        }

        this.persistRefreshedAuthState(newAuthState);

        return this.authenticationService
          .fetchActiveUser$(newAuthState.userId, newAuthState.tenantId)
          .pipe(map(() => newAuthState));
      }),
      catchError((error) => {
        this.logger.error('Token refresh pipeline failed', error);
        this.clearFailedRefreshState();
        return throwError(() => error);
      })
    );
  }

  private clearFailedRefreshState(): void {
    this.activeSecurityContextStateService.clearActiveAuthState();
  }

  private persistRefreshedAuthState(newAuthState: AuthState): void {
    this.entity = newAuthState;
    this.activeSecurityContextStateService.setActiveAuthState(newAuthState);
    this.unityCookieService.setAuthState(newAuthState);
  }
}
